import type { SpendingConfiguration } from "../types";

export function calculateMetrics(
  configuration: SpendingConfiguration,
  currentDayNumber: number,
  spendByDay: Record<string, number>
) {
  const originalDailyAllowance = configuration.periodAllowance / configuration.totalPeriodDays;
  const daysLeftIncludingToday = configuration.totalPeriodDays - currentDayNumber + 1;
  const spendBeforeToday = Object.entries(spendByDay)
    .filter(([day]) => Number(day) < currentDayNumber)
    .reduce((total, [, value]) => total + value, 0);
  const spendToday = spendByDay[currentDayNumber] ?? 0;
  const totalSpendSoFar = Object.values(spendByDay).reduce((total, value) => total + value, 0);
  const todayStartingBalance = configuration.periodAllowance - spendBeforeToday;
  const todayAllowance = daysLeftIncludingToday > 0 ? todayStartingBalance / daysLeftIncludingToday : 0;

  return {
    dailyAllowanceDifference: todayAllowance - originalDailyAllowance,
    daysLeftIncludingToday,
    remainingPeriodBalance: configuration.periodAllowance - totalSpendSoFar,
    remainingToday: todayAllowance - spendToday,
    spendToday,
    todayAllowance
  };
}

export function getTodayDisplay(metrics: ReturnType<typeof calculateMetrics>) {
  if (metrics.todayAllowance <= 0 && metrics.spendToday <= 0) {
    return {
      isNegative: false,
      message: () => "No funds available today",
      remainingAmount: 0,
      statusClass: "status-danger"
    };
  }

  if (metrics.remainingToday < 0) {
    return {
      isNegative: true,
      message: (formatter: Intl.NumberFormat) =>
        `Over today’s allowance by ${formatter.format(Math.abs(metrics.remainingToday))}`,
      remainingAmount: metrics.remainingToday,
      statusClass: "status-danger"
    };
  }

  return {
    isNegative: false,
    message: () => "Money left for today",
    remainingAmount: metrics.remainingToday,
    statusClass: "status-good"
  };
}
